"use client";

import { useEffect, useRef } from "react";
import { AssetLibrary } from "@/components/assets/AssetLibrary";
import { Inspector } from "@/components/inspector/Inspector";
import { SceneViewport } from "@/components/viewport/SceneViewport";
import { SceneHierarchy } from "./SceneHierarchy";
import { TopToolbar } from "./TopToolbar";
import { useEditorStore } from "@/stores/editorStore";
import { useProjectStore } from "@/stores/projectStore";
import { useSceneStore } from "@/stores/sceneStore";
import { createProject, loadLocalProject, saveLocalProject } from "@/services/projectStorage";
import { Timeline } from "@/components/timeline/Timeline";
import { useTimelineStore } from "@/stores/timelineStore";
import { useBackendStore } from "@/stores/backendStore";
import { CloudPanel } from "@/components/project/CloudPanel";
import { FullscreenControls } from "@/components/preview/FullscreenControls";

export function EditorLayout() {
  const initialized = useRef(false);
  const objects = useSceneStore((state) => state.objects);
  const revision = useSceneStore((state) => state.revision);
  const past = useSceneStore((state) => state.past);
  const future = useSceneStore((state) => state.future);
  const removeObject = useSceneStore((state) => state.removeObject);
  const duplicateObject = useSceneStore((state) => state.duplicateObject);
  const replaceObjects = useSceneStore((state) => state.replaceObjects);
  const undo = useSceneStore((state) => state.undo);
  const redo = useSceneStore((state) => state.redo);
  const selectedObjectId = useEditorStore((state) => state.selectedObjectId);
  const selectObject = useEditorStore((state) => state.selectObject);
  const setActiveTool = useEditorStore((state) => state.setActiveTool);
  const projectId = useProjectStore((state) => state.projectId);
  const projectName = useProjectStore((state) => state.projectName);
  const hydrated = useProjectStore((state) => state.hydrated);
  const dirty = useProjectStore((state) => state.dirty);
  const lastSavedAt = useProjectStore((state) => state.lastSavedAt);
  const setProject = useProjectStore((state) => state.setProject);
  const setHydrated = useProjectStore((state) => state.setHydrated);
  const markDirty = useProjectStore((state) => state.markDirty);
  const markSaved = useProjectStore((state) => state.markSaved);
  const timelineRevision = useTimelineStore((state) => state.revision);
  const replaceTimeline = useTimelineStore((state) => state.replaceTimeline);
  const togglePlaying = useTimelineStore((state) => state.togglePlaying);
  const deleteClip = useTimelineStore((state) => state.deleteClip);
  const selectedClipId = useTimelineStore((state) => state.selectedClipId);
  const removeObjectTracks = useTimelineStore((state) => state.removeObjectTracks);
  const initializeBackend = useBackendStore((state) => state.initialize);
  const backendStatus = useBackendStore((state) => state.status);
  const fullscreenPreview = useEditorStore((state) => state.fullscreenPreview);
  const endFullscreenPreview = useEditorStore((state) => state.endFullscreenPreview);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    try {
      const project = loadLocalProject();
      if (project) { replaceObjects(project.objects, true); replaceTimeline(project.timeline, project.duration); setProject(project.id, project.name); }
    } catch { localStorage.removeItem("3d-editor-project-local-project"); }
    setHydrated(true);
  }, [replaceObjects, replaceTimeline, setHydrated, setProject]);

  useEffect(() => { void initializeBackend(); }, [initializeBackend]);

  useEffect(() => {
    const onFullscreenChange = () => { if (!document.fullscreenElement) endFullscreenPreview(); };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, [endFullscreenPreview]);

  useEffect(() => {
    if (!hydrated) return;
    markDirty();
    const timer = window.setTimeout(() => {
      saveLocalProject(createProject(projectName, useSceneStore.getState().objects, projectId, useTimelineStore.getState().tracks, useTimelineStore.getState().duration));
      markSaved();
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [hydrated, markDirty, markSaved, projectId, projectName, revision, timelineRevision]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (useEditorStore.getState().fullscreenPreview) return;
      if (["INPUT", "SELECT", "TEXTAREA", "BUTTON"].includes((event.target as HTMLElement).tagName)) return;
      const key = event.key.toLowerCase();
      if (event.ctrlKey || event.metaKey) {
        if (key === "s") { event.preventDefault(); saveLocalProject(createProject(projectName, useSceneStore.getState().objects, projectId, useTimelineStore.getState().tracks, useTimelineStore.getState().duration)); markSaved(); }
        if (key === "d" && selectedObjectId) { event.preventDefault(); const id = duplicateObject(selectedObjectId); if (id) selectObject(id); }
        if (key === "z") { event.preventDefault(); if (event.shiftKey) redo(); else undo(); }
        return;
      }
      if (event.code === "Space") { event.preventDefault(); togglePlaying(); }
      if (key === "q") setActiveTool("select");
      if (key === "w") setActiveTool("translate");
      if (key === "e") setActiveTool("rotate");
      if (key === "r") setActiveTool("scale");
      if ((event.key === "Delete" || event.key === "Backspace") && selectedClipId) { deleteClip(selectedClipId); return; }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedObjectId) {
        const hasTracks = useTimelineStore.getState().tracks.some((track) => track.objectId === selectedObjectId);
        if (hasTracks && !window.confirm("This object has timeline actions. Delete the object and all related clips?")) return;
        removeObjectTracks(selectedObjectId); removeObject(selectedObjectId); selectObject(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleteClip, duplicateObject, markSaved, projectId, projectName, redo, removeObject, removeObjectTracks, selectObject, selectedClipId, selectedObjectId, setActiveTool, togglePlaying, undo]);

  const saveStatus = dirty ? "Unsaved changes" : lastSavedAt ? `Saved ${new Date(lastSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Ready";
  return (
    <main id="scene-editor" className={`editor-shell ${fullscreenPreview ? "fullscreen-preview" : ""}`}>
      {fullscreenPreview ? <><div className="fullscreen-viewport"><SceneViewport /></div><FullscreenControls /></> : <>
        <TopToolbar />
        <div className="workspace">
          <aside className="panel left-panel"><AssetLibrary /><SceneHierarchy /></aside>
          <SceneViewport />
          <aside className="panel right-panel"><Inspector /></aside>
        </div>
        <Timeline />
        <CloudPanel />
        <footer className="statusbar">
          <span><strong>{objects.length}</strong> objects</span><span>WebGL • 1 unit = 1 meter</span>
          <span>Undo <strong>{past.length}</strong> · Redo <strong>{future.length}</strong></span>
          <span className="status-spacer" /><span>{backendStatus === "available" ? "Cloud API ready" : "Local mode"}</span><span>{saveStatus}</span><span>Phase 8 • Recording mode</span>
        </footer>
      </>}
    </main>
  );
}
